import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, DollarSign, ExternalLink, Filter, Loader2,
  Receipt, TrendingUp, Wallet,
} from "lucide-react";
import { api } from "@/lib/api";

function StatCard({ icon: Icon, label, value, hint, testId }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
          <div className="mt-2 text-2xl font-semibold tabular-nums text-white">{value}</div>
          {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
        </div>
        <div className="rounded-lg bg-amber-500/10 p-2 text-amber-400 ring-1 ring-amber-500/20">
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const cls =
    status === "paid" ? "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30"
    : status === "refunded" ? "bg-red-500/15 text-red-300 ring-red-500/30"
    : "bg-slate-800 text-slate-400 ring-slate-700";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${cls}`}>
      {status}
    </span>
  );
}

export default function AdminPayments() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ plan_code: "", company_q: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get("/platform/payments", {
          params: { limit: 200, ...(filter.plan_code ? { plan_code: filter.plan_code } : {}) },
        }),
        api.get("/platform/payments/summary"),
      ]);
      setItems(list.items || []);
      setSummary(sum);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [filter.plan_code]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!filter.company_q) return items;
    const q = filter.company_q.toLowerCase();
    return items.filter((r) => (r.company_name || "").toLowerCase().includes(q));
  }, [items, filter.company_q]);

  return (
    <div className="space-y-6" data-testid="admin-payments-page">
      <header className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/30">
          <Wallet className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">Payments</h1>
          <p className="mt-1 text-sm text-slate-400">
            Platform subscription payments captured via Stripe Checkout.
          </p>
        </div>
      </header>

      {/* Summary cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-payments-summary">
        <StatCard
          icon={TrendingUp}
          label="MRR"
          value={summary ? `${summary.mrr}` : "…"}
          hint="Monthly recurring (approx.)"
          testId="stat-mrr"
        />
        <StatCard
          icon={DollarSign}
          label="ARR"
          value={summary ? `${summary.arr}` : "…"}
          hint="Annualized (MRR × 12)"
          testId="stat-arr"
        />
        <StatCard
          icon={Receipt}
          label="Payments (30d)"
          value={summary ? summary.count_last30 : "…"}
          hint={summary ? `${summary.total_last30} total` : ""}
          testId="stat-30d-count"
        />
        <StatCard
          icon={ArrowUpRight}
          label="By currency"
          value={
            summary && Object.keys(summary.by_currency || {}).length
              ? Object.entries(summary.by_currency).map(([k, v]) => `${v} ${k}`).join(" · ")
              : "—"
          }
          testId="stat-by-currency"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Filter className="h-4 w-4" /> Filters
        </div>
        <input
          type="text"
          placeholder="Search tenant name"
          value={filter.company_q}
          onChange={(e) => setFilter({ ...filter, company_q: e.target.value })}
          data-testid="payments-filter-q"
          className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-500 focus:outline-none"
        />
        <select
          value={filter.plan_code}
          onChange={(e) => setFilter({ ...filter, plan_code: e.target.value })}
          data-testid="payments-filter-plan"
          className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 focus:border-amber-500 focus:outline-none"
        >
          <option value="">All plans</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70">
        {loading ? (
          <div className="grid place-items-center py-16 text-slate-400" data-testid="payments-loading">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="grid place-items-center py-16 text-sm text-slate-400" data-testid="payments-empty">
            No payments yet.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-slate-800 text-sm">
            <thead className="bg-slate-950/50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 text-start">Date</th>
                <th className="px-4 py-3 text-start">Tenant</th>
                <th className="px-4 py-3 text-start">Plan</th>
                <th className="px-4 py-3 text-start">Cycle</th>
                <th className="px-4 py-3 text-end">Amount</th>
                <th className="px-4 py-3 text-start">Status</th>
                <th className="px-4 py-3 text-start">Stripe</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 text-slate-200">
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`payment-row-${r.id}`} className="hover:bg-slate-900">
                  <td className="px-4 py-3 tabular-nums text-slate-400">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/tenants/${r.company_id}`}
                      className="font-medium text-slate-100 hover:text-amber-300"
                    >
                      {r.company_name || r.company_id}
                    </Link>
                  </td>
                  <td className="px-4 py-3 uppercase">{r.plan_code}</td>
                  <td className="px-4 py-3 capitalize text-slate-400">{r.billing_cycle}</td>
                  <td className="px-4 py-3 text-end font-medium tabular-nums">{r.amount} {r.currency}</td>
                  <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                  <td className="px-4 py-3">
                    <a
                      href={`https://dashboard.stripe.com/${r.stripe_session_id?.startsWith("cs_test") ? "test/" : ""}payments/${r.stripe_session_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 font-mono text-xs text-amber-400 hover:text-amber-300"
                      data-testid={`payment-stripe-link-${r.id}`}
                    >
                      <code>{(r.stripe_session_id || "").slice(0, 18)}…</code>
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
